import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { getChartColorHex } from '@/lib/utils/chartColors'
import { getPeriodUniqueVisitorTotal, stackedBarWidths } from '@/lib/calculations/breakdownTotals'
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowRight01Icon } from "@hugeicons/core-free-icons"

// Loading skeleton for gauge chart
function GaugeChartSkeleton() {
  return (
    <div className="w-full flex flex-col items-center py-4">
      <svg 
        viewBox="-60 0 520 220" 
        className="w-full max-w-[500px] h-auto"
        style={{ minHeight: '160px' }}
      >
        <defs>
          <linearGradient id="gaugeSkeletonGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.1">
              <animate attributeName="offset" values="-1;1" dur="1.5s" repeatCount="indefinite" />
            </stop>
            <stop offset="50%" stopColor="currentColor" stopOpacity="0.2">
              <animate attributeName="offset" values="-0.5;1.5" dur="1.5s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.1">
              <animate attributeName="offset" values="0;2" dur="1.5s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
        </defs>
        
        {[0, 1, 2, 3, 4].map((i) => {
          const startAngle = Math.PI - (i * 0.6)
          const endAngle = startAngle - 0.55
          const radius = 120
          const centerX = 200
          const centerY = 140
          
          const x1 = centerX + radius * Math.cos(startAngle)
          const y1 = centerY - radius * Math.sin(startAngle)
          const x2 = centerX + radius * Math.cos(endAngle)
          const y2 = centerY - radius * Math.sin(endAngle)
          
          return (
            <path
              key={i}
              d={`M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`}
              fill="none"
              stroke="url(#gaugeSkeletonGradient)"
              strokeWidth="28"
              strokeLinecap="round"
              className="text-muted-foreground"
              style={{
                opacity: 0.4 - (i * 0.06),
                animation: `pulse 2s ease-in-out infinite`,
                animationDelay: `${i * 150}ms`
              }}
            />
          )
        })}
        
        <rect
          x="160"
          y="115"
          width="80"
          height="24"
          rx="4"
          className="fill-muted/40 animate-pulse"
        />
        <rect
          x="175"
          y="148"
          width="50"
          height="12"
          rx="3"
          className="fill-muted/30 animate-pulse"
        />
      </svg>
      
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 px-4">
        {[72, 56, 64, 48, 52].map((width, i) => (
          <div 
            key={i} 
            className="flex items-center gap-2 py-1.5 px-2"
            style={{
              animation: 'fadeInUp 0.3s ease-out forwards',
              animationDelay: `${i * 60}ms`,
              opacity: 0
            }}
          >
            <div className="size-2.5 rounded-full bg-muted/50 animate-pulse" />
            <div 
              className="h-4 rounded bg-muted/40 animate-pulse"
              style={{ width: `${width}px` }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

interface ChannelsComponentProps {
  dashBoardData: any
  onFilterByParam?: (category: string, filter: string) => void
  className?: string
}

interface UTMDataMap {
  [key: string]: number
}

// Semi-circle gauge chart component
interface ChannelsGaugeChartProps {
  data: { name: string; value: number; fill: string; percentage: string }[]
  totalVisitors: number
  activeIndex: number
  setActiveIndex: (index: number) => void
  onFilter: (name: string) => void
}

function ChannelsGaugeChart({ 
  data, 
  totalVisitors, 
  activeIndex, 
  setActiveIndex, 
  onFilter 
}: ChannelsGaugeChartProps) {
  const radius = 120
  const strokeWidth = 28
  const centerX = 200
  const centerY = 140
  const startAngle = Math.PI
  const gapAngle = 0.04

  // Overlapping shares can pass 100%; scale the arcs down only then, labels keep true shares
  const widths = stackedBarWidths(data.map((item) => parseFloat(item.percentage)))
  let currentAngle = startAngle
  const segments = data.map((item, index) => {
    const percentage = parseFloat(item.percentage)
    const sweepAngle = (widths[index] / 100) * Math.PI - gapAngle
    const segmentStartAngle = currentAngle
    const segmentEndAngle = currentAngle - sweepAngle
    currentAngle = segmentEndAngle - gapAngle

    const x1 = centerX + radius * Math.cos(segmentStartAngle)
    const y1 = centerY - radius * Math.sin(segmentStartAngle)
    const x2 = centerX + radius * Math.cos(segmentEndAngle)
    const y2 = centerY - radius * Math.sin(segmentEndAngle)
    
    const largeArcFlag = sweepAngle > Math.PI ? 1 : 0
    
    const labelAngle = (segmentStartAngle + segmentEndAngle) / 2
    const labelRadius = radius + 50
    const labelX = centerX + labelRadius * Math.cos(labelAngle)
    const labelY = centerY - labelRadius * Math.sin(labelAngle)
    
    const textAnchor: 'start' | 'middle' | 'end' = labelX < centerX - 20 ? 'end' : labelX > centerX + 20 ? 'start' : 'middle'

    return {
      ...item,
      index,
      path: `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      labelX,
      labelY,
      textAnchor,
      percentage,
    }
  })

  return (
    <div className="w-full flex flex-col items-center py-4">
      <svg 
        viewBox="-60 0 520 220" 
        className="w-full max-w-[500px] h-auto"
        style={{ minHeight: '160px' }}
      >
        {segments.map((segment) => (
          <g key={segment.index}>
            <path
              d={segment.path}
              fill="none"
              stroke={segment.fill}
              strokeWidth={activeIndex === segment.index ? strokeWidth + 6 : strokeWidth}
              strokeLinecap="round"
              className="transition-all duration-200 cursor-pointer"
              style={{
                filter: activeIndex === segment.index ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' : 'none',
                opacity: activeIndex !== segment.index ? 0.6 : 1,
              }}
              onMouseEnter={() => setActiveIndex(segment.index)}
              onClick={() => onFilter(segment.name)}
            />
            
            {segment.percentage >= 5 && (
              <g
                className="transition-opacity duration-200 cursor-pointer"
                style={{
                  opacity: activeIndex !== segment.index ? 0.5 : 1,
                }}
                onMouseEnter={() => setActiveIndex(segment.index)}
                onClick={() => onFilter(segment.name)}
              >
                <text
                  x={segment.labelX}
                  y={segment.labelY}
                  textAnchor={segment.textAnchor}
                  className="fill-muted-foreground text-[11px] font-medium"
                  dominantBaseline="middle"
                >
                  {segment.name}
                </text>
              </g>
            )}
          </g>
        ))}
        
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          className="fill-foreground text-3xl font-semibold tabular-nums"
          dominantBaseline="middle"
        >
          {totalVisitors.toLocaleString()}
        </text>
        <text
          x={centerX}
          y={centerY + 18}
          textAnchor="middle"
          className="fill-muted-foreground text-xs font-medium"
          dominantBaseline="middle"
        >
          Visitors
        </text>
      </svg>
      
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 px-4">
        {data.map((item, index) => (
          <div 
            key={index} 
            className={`flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer transition-all duration-150 ${
              activeIndex === index 
                ? 'bg-muted/60' 
                : 'hover:bg-muted/30'
            }`}
            onClick={() => onFilter(item.name)}
            onMouseEnter={() => setActiveIndex(index)}
          >
            <div 
              className="size-2.5 rounded-full shrink-0"
              style={{ backgroundColor: item.fill }}
            />
            <span className={`text-sm transition-colors duration-150 ${
              activeIndex === index ? 'text-foreground font-medium' : 'text-muted-foreground'
            }`}>
              {item.name}
            </span>
            <span className="font-semibold text-foreground tabular-nums text-sm">
              {item.percentage}%
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Helper function to categorize traffic into channels
// The API sends direct visits as null, "", "(direct)" or the literal "direct"
// / "unknown". Missing the literal ones filed every direct visit under Referral
// on the Overview (Referral 72% on a site that is 42% direct).
export const DIRECT_REFERRER_VALUES = new Set(['', '(direct)', 'direct', 'unknown'])

/** One channel rule for Overview, Traffic sources and Export. */
export function categorizeChannel(referrer: string | null, medium?: string, source?: string): string {
  if (!referrer || DIRECT_REFERRER_VALUES.has(referrer.toLowerCase())) {
    return 'Direct'
  }

  const ref = referrer.toLowerCase()
  const med = (medium || '').toLowerCase()
  const src = (source || '').toLowerCase()

  if (
    med.includes('organic') ||
    ref.includes('google') && !ref.includes('ads') ||
    ref.includes('bing') ||
    ref.includes('yahoo') ||
    ref.includes('duckduckgo') ||
    ref.includes('baidu') ||
    ref.includes('yandex')
  ) {
    return 'Organic Search'
  }

  if (
    med.includes('cpc') ||
    med.includes('ppc') ||
    med.includes('paid') ||
    ref.includes('googleads') ||
    ref.includes('google.com/ads') ||
    src.includes('adwords')
  ) {
    return 'Paid Search'
  }

  if (
    med.includes('social') ||
    ref.includes('facebook') ||
    ref.includes('instagram') ||
    ref.includes('twitter') ||
    ref.includes('x.com') ||
    ref.includes('linkedin') ||
    ref.includes('youtube') ||
    ref.includes('tiktok') ||
    ref.includes('pinterest') ||
    ref.includes('reddit') ||
    ref.includes('t.co')
  ) {
    return 'Social'
  }

  if (
    med.includes('email') ||
    med.includes('newsletter') ||
    src.includes('email') ||
    src.includes('newsletter') ||
    ref.includes('mail')
  ) {
    return 'Email'
  }

  if (
    med.includes('display') ||
    med.includes('banner') ||
    med.includes('cpm')
  ) {
    return 'Display'
  }

  return 'Referral'
}

export default function ChannelsComponent({
  dashBoardData,
  onFilterByParam,
  className = ''
}: ChannelsComponentProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  
  const getBasePath = () => {
    if (params.workspaceId) return `/workspace/${params.workspaceId}`
    if (params.propertySlugId) return `/p/${params.propertySlugId}`
    if (params.domain) return `/d/${params.domain}`
    return ''
  }
  const basePath = getBasePath()
  
  const [activeIndex, setActiveIndex] = useState(0)
  const [channelData, setChannelData] = useState<UTMDataMap>({})

  // Process channel data from referrer groups
  useEffect(() => {
    if (!dashBoardData?.metrics?.aggregate) return

    const newChannelData: UTMDataMap = {}

    const sourceData = dashBoardData.metrics.aggregate
      ?.filter((row: any) => row.referer_group_aggregate)
      ?.sort((a: any, b: any) => Number(b.count) - Number(a.count)) || []

    if (sourceData.length > 0) {
      sourceData.forEach((row: any) => {
        const channel = categorizeChannel(row.referer_group, row.utm_medium, row.utm_source)
        const count = Number(row.count)
        newChannelData[channel] = (newChannelData[channel] || 0) + count
      })
    }

    setChannelData(newChannelData)
  }, [dashBoardData])

  // Prepare chart data
  const { gaugeData, totalVisitors } = useMemo(() => {
    const entries = Object.entries(channelData)
    if (!entries.length) {
      return { gaugeData: [], totalVisitors: 0 }
    }

    const sortedEntries = entries.sort((a, b) => b[1] - a[1])
    // Deduped period total, not the row sum: multi-channel visitors sit in several rows
    const total =
      getPeriodUniqueVisitorTotal(dashBoardData?.metrics) ||
      sortedEntries.reduce((sum, [, value]) => sum + value, 0)

    const data = sortedEntries.map(([name, value], index) => ({
      name,
      value,
      fill: getChartColorHex(index),
      percentage: total > 0 ? ((value / total) * 100).toFixed(1) : '0',
    }))

    return { gaugeData: data, totalVisitors: total }
  }, [channelData, dashBoardData])

  const navigateToChannels = () => {
    navigate(`${basePath}/channels${location.search}`)
  }

  const handleFilter = (title: string) => {
    if (onFilterByParam) {
      onFilterByParam('Channels', title)
    }
  }

  const isLoading = !dashBoardData?.metrics

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="border-b border-border/40 pb-3">
          <div className="flex items-center justify-between gap-3 w-full">
            <h3 className="text-sm font-medium text-foreground">Channels</h3>
            <div className="h-7 w-16 rounded-md bg-muted/30 animate-pulse" />
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <GaugeChartSkeleton />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="border-b border-border/40 pb-3">
        <div className="flex items-center justify-between gap-3 w-full">
          <h3 className="text-sm font-medium text-foreground">Channels</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={navigateToChannels}
            className="view-all-btn"
          >
            <span>View all</span>
            <HugeiconsIcon icon={ArrowRight01Icon} size={12} className="opacity-60" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {gaugeData.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            No data to display. Try a different date range.
          </div>
        ) : (
          <ChannelsGaugeChart 
            data={gaugeData}
            totalVisitors={totalVisitors}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            onFilter={handleFilter}
          />
        )}
      </CardContent>
    </Card>
  )
}

